import React, { useState, useEffect } from 'react';
import { cityAPI } from '../../services/api';
import Navbar from '../../components/Navbar';

const CityManagement = () => {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCity, setEditingCity] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    state: '',
    isActive: true,
    monthlySubscriptionFee: 999,
    freeTrialDays: 30
  });
  const [search, setSearch] = useState('');
  const [filterState, setFilterState] = useState('');
  const [states, setStates] = useState([]);
  const [indianStates, setIndianStates] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);
  const [editingFee, setEditingFee] = useState(null);
  const [tempFee, setTempFee] = useState('');
  const [editingTrialDays, setEditingTrialDays] = useState(null);
  const [tempTrialDays, setTempTrialDays] = useState('');

  useEffect(() => {
    fetchCities();
    fetchStates();
  }, [search, filterState]);

  const fetchCities = async () => {
    try {
      setLoading(true);
      const response = await cityAPI.getAllCities({ search, state: filterState });
      setCities(response.data.data.cities);
    } catch (error) {
      alert('Failed to fetch cities');
    } finally {
      setLoading(false);
    }
  };

  const fetchStates = async () => {
    try {
      const response = await cityAPI.getStates();
      setStates(response.data.data.states);
    } catch (error) {
      console.error('Failed to fetch states');
    }
  };

  const fetchIndianStates = async () => {
    try {
      const response = await cityAPI.getIndianStates();
      setIndianStates(response.data.data.states);
    } catch (error) {
      console.error('Failed to fetch Indian states');
    }
  };

  const fetchCitiesByState = async (state) => {
    try {
      const response = await cityAPI.getCitiesByState(state);
      setAvailableCities(response.data.data.cities);
    } catch (error) {
      console.error('Failed to fetch cities');
      setAvailableCities([]);
    }
  };

  const handleStateChange = (state) => {
    setFormData({ ...formData, state, name: '' });
    if (state) {
      fetchCitiesByState(state);
    } else {
      setAvailableCities([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCity) {
        await cityAPI.updateCity(editingCity._id, formData);
        alert('City updated successfully');
      } else {
        await cityAPI.addCity(formData);
        alert('City added successfully');
      }
      setShowModal(false);
      setFormData({ name: '', state: '', isActive: true, monthlySubscriptionFee: 999, freeTrialDays: 30 });
      setEditingCity(null);
      fetchCities();
      fetchStates();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to save city');
    }
  };

  const handleEdit = (city) => {
    setEditingCity(city);
    setFormData({
      name: city.name,
      state: city.state,
      isActive: city.isActive,
      monthlySubscriptionFee: city.monthlySubscriptionFee || 999,
      freeTrialDays: city.freeTrialDays ?? 30
    });
    fetchIndianStates();
    fetchCitiesByState(city.state);
    setShowModal(true);
  };

  const handleFeeEdit = (city) => {
    setEditingFee(city._id);
    setTempFee(city.monthlySubscriptionFee?.toString() || '999');
  };

  const handleFeeSave = async (cityId) => {
    try {
      const fee = parseInt(tempFee, 10);
      if (isNaN(fee) || fee < 0) {
        alert('Please enter a valid non-negative fee amount');
        return;
      }
      await cityAPI.updateCitySubscriptionFee(cityId, fee);
      alert('Subscription fee updated successfully');
      setEditingFee(null);
      setTempFee('');
      fetchCities();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to update subscription fee');
    }
  };

  const handleFeeCancel = () => {
    setEditingFee(null);
    setTempFee('');
  };

  const handleTrialDaysEdit = (city) => {
    setEditingTrialDays(city._id);
    setTempTrialDays((city.freeTrialDays ?? 30).toString());
  };

  const handleTrialDaysSave = async (cityId) => {
    try {
      const days = parseInt(tempTrialDays, 10);
      if (isNaN(days) || days < 0 || days > 365) {
        alert('Please enter a valid number between 0 and 365');
        return;
      }
      await cityAPI.updateCity(cityId, { freeTrialDays: days });
      alert('Free trial days updated successfully');
      setEditingTrialDays(null);
      setTempTrialDays('');
      fetchCities();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to update free trial days');
    }
  };

  const handleTrialDaysCancel = () => {
    setEditingTrialDays(null);
    setTempTrialDays('');
  };

  const handleAddCity = () => {
    setShowModal(true);
    fetchIndianStates();
  };

  const handleToggleStatus = async (cityId) => {
    try {
      await cityAPI.toggleCityStatus(cityId);
      fetchCities();
    } catch (error) {
      alert('Failed to toggle city status');
    }
  };

  const handleDelete = async (cityId) => {
    if (!window.confirm('Are you sure you want to delete this city?')) return;

    try {
      await cityAPI.deleteCity(cityId);
      alert('City deleted successfully');
      fetchCities();
      fetchStates();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to delete city');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCity(null);
    setFormData({ name: '', state: '', isActive: true, monthlySubscriptionFee: 999, freeTrialDays: 30 });
    setAvailableCities([]);
  };

  return (
    <div style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)', minHeight: '100vh' }}>
      <Navbar />
      <div className="container" style={{ padding: 'var(--space-lg)', maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 'var(--space-lg)'
      }}>
        <h1 style={{
          fontSize: 'var(--font-size-2xl)',
          fontWeight: 'var(--font-weight-bold)',
          color: 'var(--color-text-primary)'
        }}>
          🏙️ City Management
        </h1>
        <button
          onClick={handleAddCity}
          className="btn btn-primary"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-sm)'
          }}
        >
          <span>➕</span>
          <span>Add City</span>
        </button>
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: 'var(--space-md)',
        marginBottom: 'var(--space-lg)',
        flexWrap: 'wrap'
      }}>
        <input
          type="text"
          placeholder="Search cities..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: 1,
            minWidth: '250px',
            padding: 'var(--space-md)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-border)',
            fontSize: 'var(--font-size-md)'
          }}
        />
        <select
          value={filterState}
          onChange={(e) => setFilterState(e.target.value)}
          style={{
            padding: 'var(--space-md)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-border)',
            fontSize: 'var(--font-size-md)',
            minWidth: '200px'
          }}
        >
          <option value="">All States</option>
          {states.map((state, index) => (
            <option key={index} value={state}>{state}</option>
          ))}
        </select>
      </div>

      {/* Cities Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>
          <div className="loading-spinner"></div>
        </div>
      ) : cities.length === 0 ? (
        <div className="card" style={{
          textAlign: 'center',
          padding: 'var(--space-2xl)'
        }}>
          <p style={{
            fontSize: 'var(--font-size-lg)',
            color: 'var(--color-text-secondary)'
          }}>
            No cities found
          </p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse'
          }}>
            <thead>
              <tr style={{
                background: 'var(--color-background-secondary)',
                borderBottom: '2px solid var(--color-border)'
              }}>
                <th style={{ padding: 'var(--space-md)', textAlign: 'left' }}>City</th>
                <th style={{ padding: 'var(--space-md)', textAlign: 'left' }}>State</th>
                <th style={{ padding: 'var(--space-md)', textAlign: 'center' }}>Monthly Fee</th>
                <th style={{ padding: 'var(--space-md)', textAlign: 'center' }}>Free Trial</th>
                <th style={{ padding: 'var(--space-md)', textAlign: 'center' }}>Status</th>
                <th style={{ padding: 'var(--space-md)', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {cities.map((city) => (
                <tr key={city._id} style={{
                  borderBottom: '1px solid var(--color-border)'
                }}>
                  <td style={{ padding: 'var(--space-md)' }}>
                    <span style={{ fontWeight: '600' }}>{city.name}</span>
                  </td>
                  <td style={{ padding: 'var(--space-md)' }}>
                    {city.state}
                  </td>
                  <td style={{ padding: 'var(--space-md)', textAlign: 'center' }}>
                    {editingFee === city._id ? (
                      <div style={{ display: 'flex', gap: 'var(--space-xs)', justifyContent: 'center', alignItems: 'center' }}>
                        <span style={{ fontWeight: '600' }}>₹</span>
                        <input
                          type="number"
                          value={tempFee}
                          onChange={(e) => setTempFee(e.target.value)}
                          style={{
                            width: '80px',
                            padding: 'var(--space-xs)',
                            borderRadius: 'var(--radius-sm)',
                            border: '1px solid var(--color-primary)',
                            fontSize: 'var(--font-size-sm)',
                            textAlign: 'center'
                          }}
                          min="0"
                        />
                        <button
                          onClick={() => handleFeeSave(city._id)}
                          style={{
                            padding: 'var(--space-xs)',
                            background: 'var(--color-success)',
                            color: 'white',
                            border: 'none',
                            borderRadius: 'var(--radius-sm)',
                            cursor: 'pointer'
                          }}
                        >
                          ✓
                        </button>
                        <button
                          onClick={handleFeeCancel}
                          style={{
                            padding: 'var(--space-xs)',
                            background: 'var(--color-danger)',
                            color: 'white',
                            border: 'none',
                            borderRadius: 'var(--radius-sm)',
                            cursor: 'pointer'
                          }}
                        >
                          ✗
                        </button>
                      </div>
                    ) : (
                      <div
                        onClick={() => handleFeeEdit(city)}
                        style={{
                          cursor: 'pointer',
                          padding: 'var(--space-xs) var(--space-md)',
                          borderRadius: 'var(--radius-md)',
                          background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                          color: 'white',
                          fontWeight: '600',
                          display: 'inline-block'
                        }}
                        title="Click to edit monthly subscription fee"
                      >
                        ₹{city.monthlySubscriptionFee || 999}/mo
                      </div>
                    )}
                  </td>
                  <td style={{ padding: 'var(--space-md)', textAlign: 'center' }}>
                    {editingTrialDays === city._id ? (
                      <div style={{ display: 'flex', gap: 'var(--space-xs)', justifyContent: 'center', alignItems: 'center' }}>
                        <input
                          type="number"
                          value={tempTrialDays}
                          onChange={(e) => setTempTrialDays(e.target.value)}
                          style={{
                            width: '60px',
                            padding: 'var(--space-xs)',
                            borderRadius: 'var(--radius-sm)',
                            border: '1px solid var(--color-primary)',
                            fontSize: 'var(--font-size-sm)',
                            textAlign: 'center'
                          }}
                          min="0"
                          max="365"
                        />
                        <span style={{ fontSize: 'var(--font-size-sm)' }}>days</span>
                        <button
                          onClick={() => handleTrialDaysSave(city._id)}
                          style={{
                            padding: 'var(--space-xs)',
                            background: 'var(--color-success)',
                            color: 'white',
                            border: 'none',
                            borderRadius: 'var(--radius-sm)',
                            cursor: 'pointer'
                          }}
                        >
                          ✓
                        </button>
                        <button
                          onClick={handleTrialDaysCancel}
                          style={{
                            padding: 'var(--space-xs)',
                            background: 'var(--color-danger)',
                            color: 'white',
                            border: 'none',
                            borderRadius: 'var(--radius-sm)',
                            cursor: 'pointer'
                          }}
                        >
                          ✗
                        </button>
                      </div>
                    ) : (
                      <div
                        onClick={() => handleTrialDaysEdit(city)}
                        style={{
                          cursor: 'pointer',
                          padding: 'var(--space-xs) var(--space-md)',
                          borderRadius: 'var(--radius-md)',
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          color: 'white',
                          fontWeight: '600',
                          display: 'inline-block'
                        }}
                        title="Click to edit free trial days"
                      >
                        {city.freeTrialDays ?? 30} days
                      </div>
                    )}
                  </td>
                  <td style={{ padding: 'var(--space-md)', textAlign: 'center' }}>
                    <span style={{
                      padding: 'var(--space-xs) var(--space-md)',
                      borderRadius: 'var(--radius-full)',
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: '600',
                      background: city.isActive
                        ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                        : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                      color: 'white'
                    }}>
                      {city.isActive ? '✓ Active' : '✗ Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: 'var(--space-md)' }}>
                    <div style={{
                      display: 'flex',
                      gap: 'var(--space-sm)',
                      justifyContent: 'center'
                    }}>
                      <button
                        onClick={() => handleEdit(city)}
                        className="btn btn-primary"
                        style={{
                          padding: 'var(--space-sm) var(--space-md)',
                          fontSize: 'var(--font-size-sm)'
                        }}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => handleToggleStatus(city._id)}
                        className={city.isActive ? 'btn btn-warning' : 'btn btn-success'}
                        style={{
                          padding: 'var(--space-sm) var(--space-md)',
                          fontSize: 'var(--font-size-sm)'
                        }}
                      >
                        {city.isActive ? '⏸️ Deactivate' : '▶️ Activate'}
                      </button>
                      <button
                        onClick={() => handleDelete(city._id)}
                        className="btn btn-danger"
                        style={{
                          padding: 'var(--space-sm) var(--space-md)',
                          fontSize: 'var(--font-size-sm)'
                        }}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{
              fontSize: 'var(--font-size-xl)',
              fontWeight: 'var(--font-weight-bold)',
              marginBottom: 'var(--space-lg)'
            }}>
              {editingCity ? 'Edit City' : 'Add New City'}
            </h2>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 'var(--space-md)' }}>
                <label style={{
                  display: 'block',
                  marginBottom: 'var(--space-sm)',
                  fontWeight: '600'
                }}>
                  State *
                </label>
                <select
                  value={formData.state}
                  onChange={(e) => handleStateChange(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: 'var(--space-md)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-border)',
                    fontSize: 'var(--font-size-md)'
                  }}
                >
                  <option value="">Select State</option>
                  {indianStates.map((state, index) => (
                    <option key={index} value={state}>{state}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: 'var(--space-md)' }}>
                <label style={{
                  display: 'block',
                  marginBottom: 'var(--space-sm)',
                  fontWeight: '600'
                }}>
                  City Name *
                </label>
                <select
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  disabled={!formData.state}
                  style={{
                    width: '100%',
                    padding: 'var(--space-md)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-border)',
                    fontSize: 'var(--font-size-md)',
                    backgroundColor: !formData.state ? '#f5f5f5' : 'white',
                    cursor: !formData.state ? 'not-allowed' : 'pointer'
                  }}
                >
                  <option value="">{formData.state ? 'Select City' : 'Select State First'}</option>
                  {availableCities.map((city, index) => (
                    <option key={index} value={city}>{city}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: 'var(--space-md)' }}>
                <label style={{
                  display: 'block',
                  marginBottom: 'var(--space-sm)',
                  fontWeight: '600'
                }}>
                  Monthly Subscription Fee (₹) *
                </label>
                <input
                  type="number"
                  value={formData.monthlySubscriptionFee}
                  onChange={(e) => setFormData({ ...formData, monthlySubscriptionFee: parseInt(e.target.value, 10) || 0 })}
                  required
                  min="0"
                  style={{
                    width: '100%',
                    padding: 'var(--space-md)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-border)',
                    fontSize: 'var(--font-size-md)'
                  }}
                />
                <small style={{ color: 'var(--color-text-secondary)', marginTop: 'var(--space-xs)', display: 'block' }}>
                  Monthly subscription fee for restaurants in this city (Zero commission model)
                </small>
              </div>

              <div style={{ marginBottom: 'var(--space-md)' }}>
                <label style={{
                  display: 'block',
                  marginBottom: 'var(--space-sm)',
                  fontWeight: '600'
                }}>
                  Free Trial Days *
                </label>
                <input
                  type="number"
                  value={formData.freeTrialDays}
                  onChange={(e) => setFormData({ ...formData, freeTrialDays: parseInt(e.target.value, 10) || 0 })}
                  required
                  min="0"
                  max="365"
                  style={{
                    width: '100%',
                    padding: 'var(--space-md)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-border)',
                    fontSize: 'var(--font-size-md)'
                  }}
                />
                <small style={{ color: 'var(--color-text-secondary)', marginTop: 'var(--space-xs)', display: 'block' }}>
                  Number of free trial days for new restaurants in this city (0-365 days)
                </small>
              </div>

              <div style={{ marginBottom: 'var(--space-lg)' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-sm)',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    style={{
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer'
                    }}
                  />
                  <span style={{ fontWeight: '600' }}>Active (Available for registration)</span>
                </label>
              </div>

              <div style={{
                display: 'flex',
                gap: 'var(--space-md)',
                justifyContent: 'flex-end'
              }}>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                >
                  {editingCity ? 'Update City' : 'Add City'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default CityManagement;
